import { hls, HlsjsAdapterOptionsInterface } from '@cbsinteractive/avia-js-hls';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';

const MANIFEST_RETRY_MAX = 1;
const MANIFEST_RETRY_TIMEOUT_MS = 24000;
const MANIFEST_RETRY_BASE_DELAY_MS = 1000;

interface HlsLoaderResponse {
	url: string;
	data?: string | ArrayBuffer | object;
	code?: number;
}

interface HlsRetryConfig {
	maxNumRetry: number;
	retryDelayMs: number;
	maxRetryDelayMs: number;
	shouldRetry?: ( retryConfig: HlsRetryConfig | null | undefined, retryCount: number, isTimeout: boolean, loaderResponse: HlsLoaderResponse | undefined, retry: boolean ) => boolean;
}

interface HlsLoadPolicy {
	default: {
		maxTimeToFirstByteMs: number;
		maxLoadTimeMs: number;
		timeoutRetry: HlsRetryConfig;
		errorRetry: HlsRetryConfig;
	};
}

const shouldRetryManifest = ( retryConfig: HlsRetryConfig | null | undefined, retryCount: number, isTimeout: boolean, loaderResponse: HlsLoaderResponse | undefined, retry: boolean ): boolean => {
	if ( retry ) { return true; }
	const maxRetries = retryConfig?.maxNumRetry ?? MANIFEST_RETRY_MAX;
	if ( retryCount >= maxRetries ) { return false; }
	if ( isTimeout ) { return true; }
	const httpStatus = loaderResponse?.code;
	if ( httpStatus === undefined ) { return false; }
	if ( ( httpStatus >= 400 && httpStatus < 500 ) || httpStatus === 0 ) { return true; }
	return false;
};

/**
 * @copyright © Paramount Streaming
 * @description Critical HLS video streaming plugin for VTG Avia player
 *
 * **Critical Streaming Infrastructure:**
 * HlsPlugin provides HLS (HTTP Live Streaming) video decode capabilities essential
 * for live content, iOS compatibility, and mobile web playback. This plugin is
 * classified as CRITICAL - video playback fails without it for HLS content.
 *
 * **Core Functionality:**
 * - **HLS Video Streaming**: HTTP Live Streaming protocol for live and VOD content
 * - **Live Content Support**: Required for live events, sports coverage, and news streams
 * - **iOS Safari Compatibility**: Essential for iOS devices that prefer HLS over DASH
 * - **Mobile Web Optimization**: Optimized streaming for mobile network conditions
 *
 * **Platform Configuration:**
 * - **Desktop/Mobile Web**: Full resilience configuration with retry logic for network issues
 * - **Smart TV/HTML5**: Minimal configuration for device-optimized performance
 * - **Retry Strategy**: Balanced retry attempts for manifests, levels, and fragments
 *
 * **HLS vs DASH Differentiation:**
 * - **HLS**: Apple-originated protocol, required for iOS Safari, preferred for live streams
 * - **DASH**: Industry standard for VOD and premium DRM content via ShakaPlugin
 * - **Complementary**: Both plugins work together to cover all content and platform needs
 *
 * **VTG Integration:**
 * Uses VTG's avia-js-hls plugin for HLS.js integration, providing robust
 * HLS streaming capabilities within the VTG player ecosystem.
 *
 * **Business Impact:**
 * Required for live content availability, iOS device compatibility, and mobile
 * web streaming functionality across the platform.
 */
export class HlsPlugin extends AviaPlugin implements IAviaPlugin {
	package = hls;
	namespaceKey = 'adapter_hls';

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'HlsPlugin'
			}
		);
	}

	/**
	 * @description Configures platform-optimized HLS streaming parameters for live and VOD content
	 *
	 * **HLS Configuration Strategy:**
	 * Implements platform-specific HLS.js configuration that optimizes streaming performance
	 * for different deployment environments and network conditions.
	 *
	 * **Configuration Elements:**
	 *
	 * **1. Dependency Management:**
	 * - **Dynamic HLS.js Loading**: Loads HLS.js library from configured CDN with cache busting
	 * - **Version Control**: Ensures consistent HLS.js version across deployments
	 * - **Development Support**: Cache busting for development and testing workflows
	 *
	 * **2. Platform-Specific Streaming Configuration:**
	 *
	 * **Desktop/Mobile Web (Resilient Configuration):**
	 * - **Manifest Loading**: 1 retry attempt with 24-second timeout for playlist requests
	 * - **Level Loading**: 1 retry attempt with 24-second timeout for quality level requests
	 * - **Fragment Loading**: 1 retry attempt with 24-second timeout for video segment requests
	 * - **Network Resilience**: Balanced retry strategy for mobile network variability
	 *
	 * **Smart TV/HTML5 (Minimal Configuration):**
	 * - **Lightweight Setup**: No additional retry configuration for constrained environments
	 * - **Device Optimization**: Reduces complexity for Smart TV platform limitations
	 *
	 * **Benefits:**
	 * - **Live Stream Reliability**: Optimized retry strategy reduces live streaming interruptions
	 * - **Mobile Performance**: Configuration tuned for mobile network conditions and battery life
	 * - **Cross-Platform Support**: Unified HLS streaming across all deployment targets
	 * - **iOS Compatibility**: Ensures proper HLS streaming on iOS Safari browsers
	 *
	 * @returns {Record<string, any>} Platform-optimized HLS.js configuration for VTG Avia player
	 */
	getFactoryParams() {
		const hlsjsURL = this.utils.String.cleanUp(
			this.utils.URL.addCacheToken(
				this.tag?.model?.config?.tag?.Imports?.params?.low_level?.hlsjs?.url?.regular,
				this.runtimeVO?.debug?.cacheBust
			)
		);

		let hlsjsAdapterConfig: Record<'hlsjs', HlsjsAdapterOptionsInterface> = { hlsjs: {} };

		switch ( this?.params?.SYSTEM?.embedType ) {
			case EmbedType.DESKTOP:
			case EmbedType.MOBILE_WEB: {
				const manifestLoadPolicy: HlsLoadPolicy = {
					default: {
						maxTimeToFirstByteMs: MANIFEST_RETRY_TIMEOUT_MS,
						maxLoadTimeMs: MANIFEST_RETRY_TIMEOUT_MS,
						timeoutRetry: {
							maxNumRetry: MANIFEST_RETRY_MAX,
							retryDelayMs: MANIFEST_RETRY_BASE_DELAY_MS,
							maxRetryDelayMs: MANIFEST_RETRY_TIMEOUT_MS
						},
						errorRetry: {
							maxNumRetry: MANIFEST_RETRY_MAX,
							retryDelayMs: MANIFEST_RETRY_BASE_DELAY_MS,
							maxRetryDelayMs: MANIFEST_RETRY_TIMEOUT_MS,
							shouldRetry: shouldRetryManifest
						}
					}
				};

				hlsjsAdapterConfig = {
					hlsjs: {
						config: {
							manifestLoadPolicy,
							playlistLoadPolicy: manifestLoadPolicy,
							fragLoadingMaxRetry: 1,
							fragLoadingMaxRetryTimeout: MANIFEST_RETRY_TIMEOUT_MS
						}
					}
				};
				break;
			}
			case EmbedType.HTML5:
				break;
			default:
				break;
		}

		// set low-level debug flag
		hlsjsAdapterConfig.hlsjs.debug = this?.runtimeVO?.debug?.lowLevel || false;

		return {
			...super.getFactoryParams(),

			dependencies: {
				hlsjs: hlsjsURL
			},
			adapter: hlsjsAdapterConfig
		};
	}
}